import { useState, useRef, useEffect, useCallback } from "react";
import axios from "axios";
import { saveDraft } from "../services/assessment.api";
import { indexedDBUploadManager } from "../utils/programming/indexedDBUploadManager";
import { offlineModeManager } from "../utils/programming/offlineModeManager";
import { isOfflineError } from "../utils/programming/offlineHandler";

/**
 * Hook for auto-saving code
 * @param {object} options - Configuration options
 * @param {string} options.code - Current code
 * @param {object} options.data - Question data
 * @param {number} options.selectedLanguage - Selected language ID
 * @param {string} options.candidateAssessmentId - Candidate assessment ID
 * @param {string} options.assessmentId - Assessment ID
 * @param {string} options.skillName - Skill name
 * @param {string} options.questionId - Question ID
 * @param {boolean} options.practiceMode - Whether in practice mode
 * @returns {object} Auto-save state and functions
 */
export const useCodeAutoSave = ({
  code,
  data,
  selectedLanguage,
  candidateAssessmentId,
  assessmentId,
  skillName,
  questionId,
  practiceMode,
  draftLoaded, // Flag to prevent saving before draft is loaded
  isReadOnly, // Flag to prevent saving when question is locked
}) => {
  const [saveState, setSaveState] = useState("idle"); // idle | saving | saved | error
  const [lastSavedAt, setLastSavedAt] = useState(null);

  const codeRef = useRef(code);
  const lastSavedPayloadRef = useRef("");
  const saveTimerRef = useRef(null);
  const lastAutoSaveRef = useRef(0);
  const autoSaveIntervalRef = useRef(null);

  const questionTitle = data?.questionTitle || null;
  const questionType = data?.type || "programming";

  // Keep codeRef updated without triggering effects
  useEffect(() => {
    codeRef.current = code;
  }, [code]);

  /**
   * Internal save execution logic
   */
  const executeSave = useCallback(
    async (currentCode, force = false, showNotification = false) => {
      // Basic validation
      if (!currentCode || isReadOnly) return;
      if (!draftLoaded) return;

      const payloadKey = `${questionId}|${selectedLanguage}|${currentCode}`;

      // Skip if content hasn't changed (unless forced)
      if (!force && lastSavedPayloadRef.current === payloadKey) {
        return;
      }

      // Handle practice mode (simulated save)
      if (practiceMode) {
        if (showNotification) {
          setSaveState("saved");
          setLastSavedAt(new Date());
        }
        return;
      }

      const now = Date.now();
      // Prevent duplicate notifications within 5 seconds for automated saves
      if (showNotification && now - lastAutoSaveRef.current < 5000) return;

      setSaveState("saving");

      try {
        await saveDraft(assessmentId, {
          candidateAssessmentId: candidateAssessmentId,
          assessmentId: assessmentId,
          skill: skillName || "Programming",
          questionId: questionId,
          languageId: selectedLanguage,
          code: currentCode,
        });

        // ✅ If draft was successfully saved, remove from IndexedDB if it exists
        if (offlineModeManager.isOfflineModeSupported()) {
          try {
            await indexedDBUploadManager.markDraftCompleted(
              questionId,
              selectedLanguage,
            );
          } catch (cleanupError) {
            console.warn(
              "Error cleaning up draft from IndexedDB:",
              cleanupError,
            );
          }
        }

        lastSavedPayloadRef.current = payloadKey;
        if (showNotification) {
          lastAutoSaveRef.current = now;
        }
        setSaveState("saved");
        setLastSavedAt(new Date());
      } catch (error) {
        // Ignore cancellation errors
        if (error?.cancelled || axios.isCancel(error)) {
          return;
        }

        console.error("Auto-save failed:", error);

        // ✅ Store draft in IndexedDB if offline
        const isOffline = isOfflineError(error);
        if (isOffline && offlineModeManager.isOfflineModeSupported()) {
          try {
            await indexedDBUploadManager.storePendingDraft(questionId, {
              languageId: selectedLanguage,
              code: currentCode,
              candidateAssessmentId: candidateAssessmentId,
              assessmentId: assessmentId,
              skill: skillName || "Programming",
              questionTitle,
              questionType,
            });

            if (showNotification) {
              setSaveState("saved"); // Show as saved even though queued
              setLastSavedAt(new Date());
            }
          } catch (storageError) {
            console.error("Error storing draft in IndexedDB:", storageError);
            setSaveState("error");
          }
        } else {
          setSaveState("error");
        }
      }
    },
    [
      assessmentId,
      candidateAssessmentId,
      draftLoaded,
      isReadOnly,
      practiceMode,
      questionId,
      questionTitle,
      questionType,
      selectedLanguage,
      skillName,
    ],
  );

  /**
   * Public auto-save function (can be called manually)
   */
  const autoSaveCode = useCallback(
    (showNotification = false) => {
      // Force save the current code
      return executeSave(codeRef.current, true, showNotification);
    },
    [executeSave],
  );

  /**
   * Interval-based Auto-save (every 30 seconds)
   * Depend ONLY on read-only state, NOT on code changes.
   */
  useEffect(() => {
    if (!isReadOnly) {
      autoSaveIntervalRef.current = setInterval(() => {
        // Use requestIdleCallback for non-critical auto-save if available
        if (window.requestIdleCallback) {
          window.requestIdleCallback(() => {
            executeSave(codeRef.current, false, true);
          });
        } else {
          setTimeout(() => executeSave(codeRef.current, false, true), 0);
        }
      }, 30000);

      return () => {
        if (autoSaveIntervalRef.current) {
          clearInterval(autoSaveIntervalRef.current);
        }
      };
    }
  }, [isReadOnly, executeSave]);

  /**
   * Debounced Auto-save on code changes
   */
  useEffect(() => {
    if (!questionId || !selectedLanguage) return;
    if (!draftLoaded || isReadOnly || practiceMode) return;

    const payloadKey = `${questionId}|${selectedLanguage}|${code}`;
    // Don't schedule if content matches last save
    if (lastSavedPayloadRef.current === payloadKey) return;

    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    setSaveState("idle");

    saveTimerRef.current = setTimeout(() => {
      executeSave(code, false, false);
    }, 1200);

    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, [
    code,
    questionId,
    selectedLanguage,
    draftLoaded,
    isReadOnly,
    practiceMode,
    executeSave,
  ]);

  return {
    saveState,
    lastSavedAt,
    autoSaveCode,
  };
};
